import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { format } from "date-fns";
import { CalendarIcon, Paperclip, FileText, X, Eye, EyeOff } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";

import { MEDICATION_RULES, feeFor, quantitiesFor } from "@/lib/pp/constants";
import { api } from "@/lib/pp/api";
import { toLocalYMD, fromYMD } from "@/lib/pp/dates";

const MEDS = Object.keys(MEDICATION_RULES);
const MONTH_OPTIONS = ["1", "2", "3", "All"];

const emptyForm = {
  medication: "",
  quantity: "",
  months: "",
  fee: "",
  payment_date: null,
  pin: "",
};

const REQUIRED = ["medication", "quantity", "months", "fee", "pin"];

/**
 * Dispensing record dialog. A patient can have multiple records (2-3 per
 * profile typically). Each record captures medication + qty + months + fee +
 * payment date, and optionally an attached invoice PDF.
 */
export default function RecordFormDialog({
  open,
  onOpenChange,
  editing,
  patientId,
  clientId,
  defaultFee,           // per-patient override — legacy Harris patients = $250
  existingRecords = [],
  onSaved,
}) {
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState({});
  const [file, setFile] = useState(null);
  const [submittedDate, setSubmittedDate] = useState(() => toLocalYMD(new Date()));
  const [submittedDateOpen, setSubmittedDateOpen] = useState(false);
  const [showPin, setShowPin] = useState(false);
  const [payDateOpen, setPayDateOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({
        medication: editing.medication || "",
        quantity: editing.quantity ?? "",
        months: editing.months || "",
        fee: editing.fee ?? "",
        payment_date: editing.payment_date || null,
        pin: "",
      });
    } else {
      setForm(emptyForm);
    }
    setFile(null);
    setSubmittedDate(toLocalYMD(new Date()));
    setErrors({});
    setShowPin(false);
  }, [open, editing]);

  const setField = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((prev) => (prev[k] ? { ...prev, [k]: false } : prev));
  };
  const upd = (k) => (e) => setField(k, e?.target?.value ?? e);
  const errCls = (k) =>
    errors[k]
      ? "border-[#B84A4A] ring-1 ring-[#B84A4A]/40 focus-visible:ring-[#B84A4A]"
      : "";

  const quantities = quantitiesFor(form.medication);
  const isRuleMed = !!MEDICATION_RULES[form.medication];
  const isMaviret = form.medication === "Maviret";

  // Treatment shape depends on the drug:
  //   Maviret     → 2-month course. Single = 28/$SINGLE_FEE. Full = 56/$FULL_FEE.
  //   All others  → 3-month course. Single = 28/$SINGLE_FEE. Full = 84/$FULL_FEE.
  // Legacy patients grandfathered on an older rate (e.g. Harris pharmacy's
  // two pre-existing clients at $250 instead of the current $350) supply
  // that historical amount via `defaultFee`. Full-course = single × months.
  const SINGLE_FEE = Number(defaultFee) > 0 ? Number(defaultFee) : 350;
  const FULL_QTY = isMaviret ? 56 : 84;
  const FULL_FEE = isMaviret ? SINGLE_FEE * 2 : SINGLE_FEE * 3;
  const SINGLE_MONTH_TAGS = isMaviret ? ["1", "2"] : ["1", "2", "3"];
  const monthOptionsForMed = isMaviret
    ? MONTH_OPTIONS.filter((m) => m !== "3")
    : MONTH_OPTIONS;

  // Months-driven quantity/fee lock:
  //   months = 1 / 2 / 3 → quantity 28, fee $350
  //   months = All       → quantity = FULL_QTY, fee = FULL_FEE
  const singleMonthMode = SINGLE_MONTH_TAGS.includes(form.months);
  const allMonthsMode = form.months === "All";
  const monthsDrivenLock = singleMonthMode || allMonthsMode;
  // Quantity dropdown always shows both valid options for the drug so the
  // pharmacy can switch between single-month and full-course without
  // having to cancel and start over. The months-driven effect above will
  // realign the months tag whenever quantity changes.
  const availableQuantities = quantities || [];

  useEffect(() => {
    if (singleMonthMode) {
      setForm((f) => {
        const wantF = String(SINGLE_FEE);
        if (String(f.quantity) === "28" && String(f.fee) === wantF) return f;
        return { ...f, quantity: 28, fee: SINGLE_FEE };
      });
    } else if (allMonthsMode) {
      setForm((f) => {
        const wantQ = String(FULL_QTY);
        const wantF = String(FULL_FEE);
        if (String(f.quantity) === wantQ && String(f.fee) === wantF) return f;
        return { ...f, quantity: FULL_QTY, fee: FULL_FEE };
      });
    }
  }, [singleMonthMode, allMonthsMode, FULL_QTY, FULL_FEE, SINGLE_FEE]);

  // Reverse direction: keep quantity and months in sync when the user
  // starts from Quantity instead of Months.
  //   qty = FULL_QTY (56 or 84) → months = "All"
  //   qty = 28                  → auto-pick the first still-available
  //                                single-month tag (M1 usually), so the
  //                                pharmacy doesn't have to hunt for it.
  useEffect(() => {
    if (!form.medication || !form.quantity) return;
    const q = Number(form.quantity);
    if (q === FULL_QTY) {
      setForm((f) => (f.months === "All" ? f : { ...f, months: "All" }));
      return;
    }
    if (q === 28) {
      setForm((f) => {
        if (SINGLE_MONTH_TAGS.includes(f.months)) return f;
        // Find the earliest single tag that satisfies uniqueness +
        // sequential rules against existing records.
        for (const t of SINGLE_MONTH_TAGS) {
          if (takenMonths.has(t) || hasAllTaken) continue;
          if (t === "2" && !takenMonths.has("1")) continue;
          if (t === "3" && (!takenMonths.has("1") || !takenMonths.has("2"))) continue;
          return { ...f, months: t };
        }
        return f;
      });
    }
  }, [form.medication, form.quantity, FULL_QTY]);

  // When the medication changes to/from Maviret, any leftover months="3"
  // must be cleared because M3 is not valid for Maviret.
  useEffect(() => {
    if (isMaviret && form.months === "3") {
      setForm((f) => ({ ...f, months: "" }));
    }
  }, [isMaviret, form.months]);

  // For the currently-selected medication, work out which month tags are
  // already used on this patient's OTHER records. Mirrors the backend
  // rules so pharmacies can't even attempt an invalid selection:
  //   - 1/2/3 usable at most once per (patient, medication)
  //   - "All" is exclusive: coexists with nothing else
  //   - Sequential: 2 needs 1, 3 needs 1 AND 2; "All" is the exception.
  const takenMonths = new Set(
    (existingRecords || [])
      .filter(
        (r) =>
          r.medication === form.medication &&
          (!editing || r.id !== editing.id),
      )
      .map((r) => String(r.months || "").trim())
      .filter(Boolean),
  );
  const hasAllTaken = takenMonths.has("All");
  const hasAnySingleTaken = SINGLE_MONTH_TAGS.some((m) => takenMonths.has(m));
  const isMonthDisabled = (m) => {
    if (takenMonths.has(m)) return true;              // exact duplicate
    if (m === "All" && hasAnySingleTaken) return true; // All blocked by 1/2/3
    if (m !== "All" && hasAllTaken) return true;       // 1/2/3 blocked by All
    // Sequential rule (All is exempt). For Maviret M3 doesn't apply.
    if (m === "2" && !takenMonths.has("1")) return true;
    if (m === "3" && (!takenMonths.has("1") || !takenMonths.has("2"))) return true;
    return false;
  };
  const availableMonths = monthOptionsForMed.filter((m) => !isMonthDisabled(m));
  // Medication is fully invoiced when the user picked one but no month
  // tag can validly be added (either "All" is on file, or 1/2/3 all exist).
  const medicationFullyInvoiced =
    !!form.medication && availableMonths.length === 0 && !editing;

  // Preview the standardised filename the backend will assign on upload:
  //   Invoice_MMDDYYYY_<CLIENT>_<MTAG>.pdf
  // Uses payment_date if set, else today. Client comes from the patient
  // prop; months tag is 'ALL' or 'M<n>'. Falls back to a generic hint
  // string when we can't yet compute a valid preview.
  const previewInvoiceName = () => {
    const client = (clientId || "").replace(/[^A-Z0-9]/gi, "").toUpperCase() || "NOID";
    const m = String(form.months || "").toUpperCase();
    const mtag = m === "ALL" ? "ALL" : m ? `M${m}` : "";
    let ymd = form.payment_date;
    try {
      const d = ymd ? fromYMD(ymd) : new Date();
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      const yyyy = String(d.getFullYear());
      return `Invoice_${mm}${dd}${yyyy}_${client}_${mtag || "M0"}.pdf`;
    } catch {
      return "";
    }
  };

  // Auto-fill fee when medication + quantity match a rule (skipped when
  // the months-driven lock is active — that path sets fee itself).
  useEffect(() => {
    if (monthsDrivenLock) return;
    if (!isRuleMed || !form.quantity) return;
    const f = feeFor(form.medication, Number(form.quantity));
    if (f !== undefined) setForm((prev) => ({ ...prev, fee: f }));
  }, [form.medication, form.quantity, isRuleMed, monthsDrivenLock]);

  const save = async () => {
    const nextErr = {};
    REQUIRED.forEach((k) => {
      const v = form[k];
      if (v === undefined || v === null || String(v).trim() === "")
        nextErr[k] = true;
    });
    if (Object.keys(nextErr).length) {
      setErrors(nextErr);
      if (nextErr.pin && Object.keys(nextErr).length === 1) {
        toast.error("Enter the authorization PIN to save");
      } else {
        toast.error("Please complete all required fields");
      }
      return;
    }
    setBusy(true);
    try {
      const payload = {
        medication: form.medication,
        quantity: Number(form.quantity) || null,
        months: form.months || null,
        fee: Number(form.fee) || null,
        payment_date: form.payment_date || null,
      };
      const authHeaders = { headers: { "X-Auth-Pin": form.pin } };
      let recordId = editing?.id;
      try {
        if (editing) {
          await api.patch(`/records/${editing.id}`, payload, authHeaders);
        } else {
          const { data } = await api.post(
            `/patients/${patientId}/records`,
            payload,
            authHeaders,
          );
          recordId = data.id;
        }
      } catch (saveErr) {
        if (saveErr?.response?.status === 401) {
          setErrors((p) => ({ ...p, pin: true }));
          toast.error("Incorrect PIN — record not saved");
          setBusy(false);
          return;
        }
        throw saveErr;
      }
      // Optional PDF upload — same PIN authorises the invoice.
      if (file && recordId) {
        const fd = new FormData();
        fd.append("file", file);
        if (submittedDate) fd.append("submitted_date", submittedDate);
        try {
          const { data: up } = await api.post(
            `/records/${recordId}/invoice`,
            fd,
            {
              headers: {
                "Content-Type": "multipart/form-data",
                "X-Auth-Pin": form.pin,
              },
            },
          );
          toast.success(`Invoice attached · ${up.filename}`, { duration: 5000 });
        } catch (uerr) {
          toast.error(
            uerr?.response?.data?.detail || "Invoice upload failed",
          );
        }
      }
      toast.success(editing ? "Record updated" : "Record added");
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "h-10";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg bg-white w-[calc(100vw-1.5rem)] overflow-x-hidden"
        data-testid="pp-record-form-dialog"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader className="text-left">
          <DialogTitle
            className="text-[#1C2B26]"
            style={{ fontFamily: "'Work Sans', system-ui, sans-serif" }}
          >
            {editing ? "Edit record" : "Add invoice record"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 py-2">
          {medicationFullyInvoiced && (
            <div
              data-testid="pp-record-fully-invoiced-error"
              className="rounded-md border border-[#B84A4A]/40 bg-[#FDF2F2] px-3 py-2 text-xs text-[#8A2E2E]"
            >
              A new record cannot be added — <b>{form.medication}</b> has
              already been fully invoiced for this patient.
            </div>
          )}
          {/* Row 1 · Medication + Drug quantity */}
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1 min-w-0">
              <Label className="text-[10px] uppercase tracking-wider text-[#4A5D55]">
                Medication *
              </Label>
              <Select value={form.medication} onValueChange={(v) => setField("medication", v)}>
                <SelectTrigger
                  className={`${inputCls} ${errCls("medication")}`}
                  data-testid="pp-record-medication"
                >
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {MEDS.map((m) => (
                    <SelectItem key={m} value={m}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1 min-w-0">
              <Label className={`text-[10px] uppercase tracking-wider ${form.medication ? "text-[#4A5D55]" : "text-[#A0A69E]"}`}>
                Drug quantity *
              </Label>
              {isRuleMed && availableQuantities.length ? (
                <Select
                  value={form.quantity ? String(form.quantity) : ""}
                  onValueChange={(v) => setField("quantity", v)}
                  disabled={!form.medication}
                >
                  <SelectTrigger
                    className={`${inputCls} ${errCls("quantity")} disabled:opacity-50 disabled:cursor-not-allowed`}
                    data-testid="pp-record-quantity"
                  >
                    <SelectValue placeholder={form.medication ? "Select" : "Med first"} />
                  </SelectTrigger>
                  <SelectContent>
                    {availableQuantities.map((q) => (
                      <SelectItem key={q} value={String(q)}>{q}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  type="number"
                  value={form.quantity}
                  onChange={upd("quantity")}
                  disabled={!form.medication}
                  placeholder={form.medication ? "" : "Med first"}
                  data-testid="pp-record-quantity"
                  className={`${inputCls} ${errCls("quantity")} disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-[#F4F2ED]`}
                />
              )}
            </div>
          </div>

          {/* Row 2 · Months + Fee */}
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1 min-w-0">
              <Label className="text-[10px] uppercase tracking-wider text-[#4A5D55]">
                Months *
              </Label>
              <Select
                value={form.months}
                onValueChange={(v) => setField("months", v)}
                disabled={medicationFullyInvoiced}
              >
                <SelectTrigger
                  className={`${inputCls} ${errCls("months")} disabled:opacity-50 disabled:cursor-not-allowed`}
                  data-testid="pp-record-months"
                >
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {availableMonths.map((m) => (
                    <SelectItem
                      key={m}
                      value={m}
                      data-testid={`pp-record-months-opt-${m}`}
                    >
                      {m}
                    </SelectItem>
                  ))}
                  {/* Editing an existing record: always keep its own tag
                      visible so it doesn't vanish from the trigger. */}
                  {editing && !availableMonths.includes(String(editing.months || "")) && editing.months && (
                    <SelectItem
                      value={String(editing.months)}
                      data-testid={`pp-record-months-opt-${editing.months}`}
                    >
                      {editing.months}
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1 min-w-0">
              <Label className="text-[10px] uppercase tracking-wider text-[#4A5D55]">
                Fee (CAD) *
              </Label>
              <Input
                type="number"
                step="0.01"
                value={form.fee}
                onChange={upd("fee")}
                readOnly={monthsDrivenLock}
                data-testid="pp-record-fee"
                className={`${inputCls} ${errCls("fee")} ${monthsDrivenLock ? "bg-[#F4F2ED] cursor-not-allowed" : ""}`}
              />
            </div>
          </div>

          {/* Row 3 · Payment date + Authorization PIN */}
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1 min-w-0">
              <Label className="text-[10px] uppercase tracking-wider text-[#4A5D55]">
                Payment date
              </Label>
              <Popover open={payDateOpen} onOpenChange={setPayDateOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    data-testid="pp-record-payment"
                    className={`${inputCls} w-full inline-flex items-center gap-1 rounded-md border border-[#E5E2DC] bg-white px-2 text-sm text-[#1C2B26]`}
                  >
                    <CalendarIcon className="h-3.5 w-3.5 shrink-0 text-[#4A5D55]" />
                    <span className="truncate flex-1 text-left">
                      {form.payment_date
                        ? format(fromYMD(form.payment_date), "MMM d, yyyy")
                        : "Not paid yet"}
                    </span>
                    {form.payment_date && (
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => {
                          e.stopPropagation();
                          setField("payment_date", null);
                        }}
                        className="text-[#4A5D55] hover:text-[#B84A4A] shrink-0"
                      >
                        <X className="h-3 w-3" />
                      </span>
                    )}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={fromYMD(form.payment_date)}
                    onSelect={(d) => {
                      setField("payment_date", toLocalYMD(d));
                      setPayDateOpen(false);
                    }}
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-1 min-w-0">
              <Label className="text-[10px] uppercase tracking-wider text-[#4A5D55]">
                Authorization PIN *
              </Label>
              <div className="relative">
                <Input
                  type={showPin ? "text" : "password"}
                  inputMode="numeric"
                  autoComplete="off"
                  value={form.pin}
                  onChange={upd("pin")}
                  placeholder="PIN"
                  data-testid="pp-record-pin"
                  className={`${inputCls} pr-8 ${errCls("pin")}`}
                />
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => setShowPin((v) => !v)}
                  aria-label={showPin ? "Hide PIN" : "Show PIN"}
                  data-testid="pp-record-pin-toggle"
                  className="absolute inset-y-0 right-2 flex items-center text-[#4A5D55] hover:text-[#0F3B2E]"
                >
                  {showPin ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {errors.pin && (
                <p className="text-[10px] text-[#B84A4A]" data-testid="pp-record-pin-error">
                  Incorrect PIN
                </p>
              )}
            </div>
          </div>

          {/* Row 4 · Invoice PDF (always last).
              File name shown is the standardised name the backend will
              assign on save (Invoice_MMDDYYYY_CLIENT_MTAG.pdf) — never
              the user's raw filename — so upload + download stay consistent. */}
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-[#4A5D55]">
              Invoice PDF
            </Label>
            <label
              className="flex items-center gap-2 h-10 px-3 rounded-md border border-dashed border-[#8EBCA9] bg-[#F5F9F7] cursor-pointer text-sm text-[#2D5A4C] min-w-0"
              data-testid="pp-record-invoice-upload"
            >
              <Paperclip className="h-3.5 w-3.5 shrink-0" />
              <span className="flex-1 min-w-0 truncate">
                {file
                  ? previewInvoiceName()
                  : editing?.invoice_filename
                  ? editing.invoice_filename
                  : "Attach PDF (optional, up to 10MB)"}
              </span>
              <input
                type="file"
                accept="application/pdf"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f && f.size > 10 * 1024 * 1024) {
                    toast.error("File too large (max 10MB)");
                    return;
                  }
                  setFile(f || null);
                }}
              />
            </label>
            {/* Submitted date — shown as soon as a file is staged so the
                pharmacy captures when the invoice was actually submitted.
                Same field the row-level upload popup uses. */}
            {file && (
              <div className="pt-2">
                <Label className="text-[10px] uppercase tracking-wider text-[#4A5D55]">
                  Submitted date *
                </Label>
                <Popover open={submittedDateOpen} onOpenChange={setSubmittedDateOpen}>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      data-testid="pp-record-submitted-date"
                      className="mt-1 h-9 w-full inline-flex items-center gap-2 rounded-md border border-[#E5E2DC] bg-white px-3 text-sm text-[#1C2B26]"
                    >
                      <CalendarIcon className="h-3.5 w-3.5 text-[#4A5D55] shrink-0" />
                      <span className="truncate flex-1 text-left">
                        {submittedDate
                          ? format(fromYMD(submittedDate), "MMM d, yyyy")
                          : "Pick date"}
                      </span>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={fromYMD(submittedDate)}
                      onSelect={(d) => {
                        if (d) setSubmittedDate(toLocalYMD(d));
                        setSubmittedDateOpen(false);
                      }}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            )}
            {editing?.invoice_filename && !file && (editing.authorized_at || editing.invoice_authorized_at) && (
              <div className="text-[11px] text-[#2D5A4C] pl-1 space-y-0.5" data-testid="pp-record-auth-block">
                <p>
                  Invoice Authorized: {new Date(editing.authorized_at || editing.invoice_authorized_at).toLocaleString()}
                </p>
                {editing.payment_date && (() => {
                  const invAuth = new Date(editing.authorized_at || editing.invoice_authorized_at);
                  const payDate = fromYMD(editing.payment_date);
                  const days = Math.round(Math.abs(payDate - invAuth) / 86400000);
                  return (
                    <p data-testid="pp-record-payment-auth">
                      Payment Authorized: {format(payDate, "MMM d, yyyy")}
                      {" · "}
                      <span className="font-medium">{days} day{days === 1 ? "" : "s"} to pay</span>
                    </p>
                  );
                })()}
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-row items-center justify-end gap-2 pt-2">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            data-testid="pp-record-cancel"
            className="h-9 px-4 text-sm"
          >
            Cancel
          </Button>
          <Button
            onClick={save}
            disabled={busy || medicationFullyInvoiced}
            className="h-9 px-4 text-sm bg-[#0F3B2E] hover:bg-[#0A2A20] text-white disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="pp-record-save"
          >
            {busy ? "Saving…" : editing ? "Save changes" : "Add record"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
